"use client";

import React from "react";
import { FilePen, ClipboardCheck, CircleCheck, ArrowRight, ArrowDown, Search, FileText, ShieldCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pipelineStages = [{
  id: "draft",
  title: "Draft",
  description: "Content is uploaded and prepared for review.",
  icon: FilePen,
  styleClass: "border-dashed border-2 border-muted-foreground/40 text-muted-foreground bg-background",
  iconClass: "text-muted-foreground/60"
}, {
  id: "review",
  title: "Review",
  description: "Your team validates content before it becomes AI-available.",
  icon: ClipboardCheck,
  styleClass: "border-solid border-2 border-amber-500/40 text-foreground bg-amber-500/5",
  iconClass: "text-amber-600"
}, {
  id: "approved",
  title: "Approved",
  description: "Only approved content is available for AI answers.",
  icon: CircleCheck,
  styleClass: "border-solid border-4 border-primary text-foreground bg-primary/5 shadow-lg shadow-primary/10 relative",
  iconClass: "text-primary",
  badge: true
}];
const aiViewColumns = [{
  id: "ai-draft",
  stage: "Draft",
  status: "Not searchable",
  description: "Draft content is excluded from the retrieval layer and cannot appear in AI answers.",
  icon: FilePen,
  cardClass: "opacity-60 grayscale border-dashed",
  iconClass: "text-muted-foreground"
}, {
  id: "ai-review",
  stage: "Review",
  status: "Not searchable",
  description: "Content under validation stays unavailable until a reviewer explicitly approves it.",
  icon: ClipboardCheck,
  cardClass: "opacity-60 grayscale border-dashed",
  iconClass: "text-muted-foreground"
}, {
  id: "ai-approved",
  stage: "Approved",
  status: "Searchable by AI",
  description: "Only approved content is indexed, retrieved, and cited in grounded answers.",
  icon: Search,
  cardClass: "ring-2 ring-primary shadow-md",
  iconClass: "text-primary"
}];
const businessPoints = [{
  id: "point-1",
  title: "Keeps retrieval grounded",
  description: "Your AI answers only from approved company knowledge, not raw uploads or unfinished edits.",
  icon: FileText
}, {
  id: "point-2",
  title: "Prevents premature answers",
  description: "Teams can upload and review content safely before it becomes available to the retrieval layer.",
  icon: ShieldCheck
}, {
  id: "point-3",
  title: "Adds control without building it yourself",
  description: "EloRAG gives you a governed RAG workflow without having to engineer the full approval system in-house.",
  icon: CircleCheck
}];
export default function DraftReviewApproved() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: pipelineRef,
    isVisible: isPipelineVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: tabsRef,
    isVisible: isTabsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="draft-review-approved" className="bg-background py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={headerRef} className={cn("text-center max-w-3xl mx-auto mb-20 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <ShieldCheck className="size-4" />
            <span>Controlled Knowledge Access</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-6">
            How EloRAG keeps answers grounded and controlled
          </h2>
          <p className="text-lg text-muted-foreground">
            EloRAG uses a governed content lifecycle so your retrieval layer only works with approved knowledge.
            Your team can upload, review, and validate content first, while the AI remains restricted to what is explicitly approved.
          </p>
        </div>

        <div ref={pipelineRef} className={cn("relative max-w-5xl mx-auto mb-24 transition-all duration-1000 delay-100 ease-out", isPipelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0">
            {pipelineStages.map((stage, i) => <React.Fragment key={stage.id}>
                <div data-index={i} className={cn("flex flex-col items-center justify-center p-8 rounded-2xl w-full md:w-72 text-center transition-all duration-500", stage.styleClass, isPipelineVisible ? "scale-100 opacity-100" : "scale-95 opacity-0")} style={{
              transitionDelay: `${i * 200}ms`
            }}>
                  {stage.badge && <div className="absolute -top-3 -right-3 bg-primary text-primary-foreground rounded-full p-1.5 shadow-sm">
                      <CircleCheck className="size-5" />
                    </div>}
                  <stage.icon className={cn("size-10 mb-4", stage.iconClass)} />
                  <h3 className="text-xl font-bold mb-2">{stage.title}</h3>
                  <p className="text-sm opacity-80">{stage.description}</p>
                </div>

                {i < pipelineStages.length - 1 && <div className="flex items-center justify-center py-4 md:py-0 md:px-4 text-muted-foreground/50">
                    <ArrowRight className="hidden md:block size-8 animate-pulse" />
                    <ArrowDown className="block md:hidden size-8 animate-pulse" />
                  </div>}
              </React.Fragment>)}
          </div>
        </div>

        <div ref={tabsRef} className={cn("max-w-6xl mx-auto transition-all duration-700 delay-300 ease-out", isTabsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-foreground mb-4">What the retrieval layer can actually access</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              EloRAG does not search everything. It searches only the approved layer of your company knowledge.
            </p>
          </div>

          <Tabs defaultValue="ai" className="w-full">
            <div className="flex justify-center mb-12">
              <TabsList className="grid w-full max-w-md grid-cols-2 h-12">
                <TabsTrigger value="ai" className="text-base">AI Retrieval View</TabsTrigger>
                <TabsTrigger value="business" className="text-base">Business Impact</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="ai" className="mt-0 outline-none">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {aiViewColumns.map((col, i) => <Card key={col.id} data-index={i} className={cn("transition-all duration-300", col.cardClass)}>
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <CardTitle className="text-lg">{col.stage}</CardTitle>
                        <col.icon className={cn("size-5", col.iconClass)} />
                      </div>
                      <CardDescription className={cn("font-semibold", col.iconClass)}>
                        {col.status}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {col.description}
                      </p>
                    </CardContent>
                  </Card>)}
              </div>
            </TabsContent>

            <TabsContent value="business" className="mt-0 outline-none">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {businessPoints.map((point, i) => <Card key={point.id} data-index={i} className="transition-all duration-300 border-border">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <CardTitle className="text-lg">{point.title}</CardTitle>
                        <point.icon className="size-5 text-primary" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {point.description}
                      </p>
                    </CardContent>
                  </Card>)}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="mt-20 text-center">
          <Button asChild size="lg" className="rounded-full px-8">
            <Link to="/SecurityAndGovernancePage">
              Explore Security & Governance
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}